// Keeps the Fast Pay batch a biller is building across the page reload that Apply overpayment
// causes. Apply overpayment posts and redirects back to the same filtered list, and a redirect
// does not bring back form values the way the browser Back button does.
//
// The batch is saved when the Apply overpayment confirmation opens, armed only when the biller
// confirms, and taken once after the reload. Opening then cancelling leaves an unarmed copy that
// is never restored and is overwritten by the next save.

const KEY = 'fastPay:batchBeforeCreditApply';

// Long enough for a slow application and redirect, short enough that an old batch never
// reappears on a later visit to the same list.
export const MAX_AGE_MS = 5 * 60 * 1000;

const defaultStorage = () => window.sessionStorage;

// Private browsing and blocked site data make sessionStorage throw. Fast Pay must keep working;
// the batch is simply not kept.
function read(storage) {
  try {
    return JSON.parse(storage.getItem(KEY));
  } catch {
    remove(storage);
    return null;
  }
}

function write(storage, value) {
  try {
    storage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Not kept; see read.
  }
}

function remove(storage) {
  try {
    storage.removeItem(KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/**
 * @param {Object} args
 * @param {string} args.url pathname and query of the Fast Pay list, so a batch only returns to the same filtered list
 * @param {string} args.applyingInvoiceId the row whose credit is being applied
 * @param {Array<Object>} args.rows one entry per selected row: its submitted fields plus remaining_balance
 */
export function saveBatch({ url, applyingInvoiceId, rows, now = Date.now(), storage = defaultStorage() }) {
  write(storage, { url, applyingInvoiceId, rows, savedAt: now, armed: false });
}

export function armBatch({ now = Date.now(), storage = defaultStorage() } = {}) {
  const snapshot = read(storage);
  if (!snapshot) return;

  write(storage, { ...snapshot, armed: true, savedAt: now });
}

/**
 * Returns the batch to restore and removes it, so it is restored at most once.
 *
 * @returns {{ applyingInvoiceId: string, rows: Array<Object> } | null}
 */
export function takeBatch({ url, now = Date.now(), storage = defaultStorage() }) {
  const snapshot = read(storage);
  if (!snapshot) return null;

  remove(storage);
  if (!snapshot.armed || snapshot.url !== url || now - snapshot.savedAt > MAX_AGE_MS) return null;

  return { applyingInvoiceId: snapshot.applyingInvoiceId, rows: snapshot.rows };
}
